import React from 'react';
import { Facebook, Twitter, Linkedin, Instagram, Mail, ArrowUpRight } from 'lucide-react';

export default function Footer() {
  const quickLinks = [
    { name: 'Services', href: '#services' },
    { name: 'Case Study', href: '#case-study' },
    { name: 'Portfolio', href: '#portfolio' },
    { name: 'Why Choose Us', href: '#why-us' },
    { name: 'Process Blueprint', href: '#process' },
    { name: 'Contact Sales', href: '#contact' }
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61585929133996', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/code-credence/', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://instagram.com/codecredence', label: 'Instagram' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="bg-[#EFE7DD] border-t border-[#8B6B4A]/15 text-[#3E2F24]" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Col 1: Brand Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-[#8B6B4A] via-[#C8A97E] to-[#D6C2A8] flex items-center justify-center">
                <span className="font-space font-bold text-white text-xs">CC</span>
              </div>
              <h2 className="text-xl font-bold font-space text-[#3E2F24]" data-testid="footer-logo">
                Code<span className="text-[#8B6B4A]">Credence</span>
              </h2>
            </div>
            <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed">
              We design and code elite web ecosystems, custom SaaS products, and management platforms for progressive companies.
            </p>
            <a
              href="mailto:info@codecredence.com"
              className="inline-flex items-center gap-2 text-sm text-[#8B6B4A] hover:text-[#3E2F24] font-poppins transition-colors"
              data-testid="footer-email-link"
            >
              <Mail className="w-4 h-4" />
              info@codecredence.com
            </a>
          </div>

          {/* Col 2: Navigation links */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#3E2F24] font-space mb-6">Navigation</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-[#6B625B] hover:text-[#3E2F24] text-xs sm:text-sm font-poppins transition-colors flex items-center gap-1 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Services Index */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#3E2F24] font-space mb-6">Capabilities</h3>
            <ul className="space-y-3 text-[#6B625B] text-xs sm:text-sm font-poppins">
              <li>Website Architecture</li>
              <li>Full Stack Engineering</li>
              <li>SaaS Workspace Platforms</li>
              <li>Salon Management & CRM</li>
              <li>DevOps & CI/CD Pipelines</li>
            </ul>
          </div>

          {/* Col 4: Social handles */}
          <div className="space-y-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#3E2F24] font-space mb-6">Connect</h3>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/60 hover:bg-[#8B6B4A]/10 border border-[#8B6B4A]/10 hover:border-[#8B6B4A]/30 flex items-center justify-center text-[#3E2F24] transition-all"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
            <p className="text-[#6B625B] text-xs font-poppins leading-relaxed">
              AI-driven insights and digital transformation articles published regularly across our social platforms.
            </p>
          </div>

        </div>

        {/* Copyrights block */}
        <div className="pt-8 border-t border-[#8B6B4A]/15 text-center text-[#6B625B] text-xs font-poppins">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} CodeCredence. All rights reserved. Architected with passion for clean software.
          </p>
        </div>
      </div>
    </footer>
  );
}
